import { RefObject } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  InputBase,
  Skeleton,
  Typography,
} from "@mui/material";
import { AddContactFields } from "models/interfaces";
import { debounceEventHandler } from "helpers/methods";
import strings from "global/constants/StringConstants";
import contactsStyles from "../Contacts.styles";
import contactsTokens, { getContactTint } from "../Contacts.tokens";
import { ChevronRightIcon, SearchGlyph } from "./ContactsIcons";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr } from "tour";

const styles = contactsStyles;
const { list } = contactsTokens;

/** "AR" for Auto Recipient — falls back to whichever name part exists. */
export const getContactInitials = (contact?: AddContactFields) => {
  const first = contact?.firstName?.trim()?.[0] ?? "";
  const last = contact?.lastName?.trim()?.[0] ?? "";
  return `${first}${last}`.toUpperCase();
};

interface ContactsListProps {
  contacts: AddContactFields[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Search text taken from the URL on first paint (uncontrolled seed). */
  initialSearchText: string;
  /** Search text currently applied to the list. */
  searchText: string;
  onSearchChange: (event: any) => void;
  /** True while the first page (or a new search) is in flight. */
  isLoading: boolean;
  /** True while the next page is being appended. */
  isPaginating: boolean;
  hasSearch: boolean;
  scrollRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
}

const ContactsList = ({
  contacts,
  selectedIndex,
  onSelect,
  initialSearchText,
  searchText,
  onSearchChange,
  isLoading,
  isPaginating,
  hasSearch,
  scrollRef,
  onScroll,
}: ContactsListProps) => {
  const isEmpty = !isLoading && contacts.length === 0;

  const getResultLabel = () => {
    if (isLoading) return "";
    return hasSearch ? `${contacts.length} found` : `${contacts.length} shown`;
  };

  const getSkeletonRows = () =>
    Array.from({ length: 8 }).map((_, index) => (
      <Box
        key={`contact-skeleton-${index}`}
        aria-hidden="true"
        sx={styles.listRow(false)}
      >
        <Skeleton variant="circular" sx={styles.skeletonAvatar} />
        <Box sx={styles.listRowText}>
          <Skeleton
            variant="text"
            width="62%"
            height={18}
            sx={styles.skeleton}
          />
          <Skeleton
            variant="text"
            width="86%"
            height={14}
            sx={styles.skeleton}
          />
        </Box>
      </Box>
    ));

  const getRows = () =>
    contacts.map((contact, index) => {
      const tint = getContactTint(index);
      const isSelected = index === selectedIndex;
      const fullName =
        `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim();
      return (
        <ButtonBase
          key={`${contact.emailId}-${index}`}
          disableRipple
          onClick={() => onSelect(index)}
          aria-current={isSelected ? "true" : undefined}
          sx={styles.listRow(isSelected)}
        >
          <Avatar
            aria-hidden="true"
            sx={styles.listAvatar(tint.bg, tint.color)}
          >
            {getContactInitials(contact)}
          </Avatar>
          {/* Long names and addresses are clipped with an ellipsis, exactly as
              the design does — no tooltip, so hovering a row stays quiet. */}
          <Box sx={styles.listRowText}>
            <Typography component="div" sx={styles.listRowName}>
              {fullName}
            </Typography>
            <Typography component="div" sx={styles.listRowEmail}>
              {contact.emailId}
            </Typography>
          </Box>
          {/* The tablet and desktop mark the open row with a dot; the phone
              opens the detail full-screen instead, so its rows carry a
              "goes somewhere" chevron and no selected state at all. */}
          {isSelected && <Box sx={styles.listRowDot} />}
          <ChevronRightIcon sx={styles.listRowChevron} />
        </ButtonBase>
      );
    });

  return (
    <Box sx={styles.listPane} {...tourAttr("contacts.list")}>
      <Box sx={styles.searchWrap}>
        <Box sx={styles.searchField}>
          <SearchGlyph sx={styles.searchIcon} />
          <InputBase
            id="contacts_search_box"
            defaultValue={initialSearchText}
            placeholder="Search contacts"
            autoComplete="off"
            sx={styles.searchInput}
            inputProps={{ "aria-label": "Search contacts" }}
            onChange={debounceEventHandler(
              onSearchChange,
              strings.SEARCH_TIME_OUT,
            )}
          />
        </Box>
      </Box>

      {/* The desktop reference dropped the "N shown" label (and the
          display-only Name sort beside it) and the phone never had one; only
          the tablet column still draws it, so it is rendered here and shown at
          `sm` alone. */}
      <Box sx={styles.listMeta} aria-hidden="true">
        <Typography component="span" sx={styles.listMetaLabel}>
          {getResultLabel()}
        </Typography>
      </Box>

      {/* Keep the SR-only status region so the result count is still announced
          on search / first load, but stay silent while more pages append during
          infinite scroll so it isn't re-announced on every page (WCAG 4.1.3). */}
      <Box
        component="span"
        role="status"
        aria-live="polite"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
        }}
      >
        {isPaginating ? "" : getResultLabel()}
      </Box>

      <Box ref={scrollRef} onScroll={onScroll} sx={styles.listScroll}>
        {isLoading && getSkeletonRows()}
        {isEmpty && (
          <EmptyState
            size="medium"
            icon={hasSearch ? "search" : "contact"}
            accent="violet"
            title={
              hasSearch
                ? `No contacts match "${searchText}"`
                : "No contacts yet"
            }
            body={
              hasSearch
                ? "Try a different name or email."
                : "Contacts are saved automatically when you send sign requests."
            }
          />
        )}
        {!isLoading && getRows()}
        {isPaginating && (
          <Box sx={styles.listLoader}>
            <CircularProgress
              size={list.loaderSize}
              aria-label="Loading more contacts"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContactsList;
